import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
// mui components
import { Box, Button, Stack, Typography } from '@mui/material';
// components
import ErrorModal from 'components/error-modal';
import PurpleButton from 'components/purple-button';
import BankingProofUpload from './banking-proof-upload';
import SectionCard from './section-card';
import StepField from './step-field';
import WelcomeGiftBlock from './welcome-gift-block';
// hooks
import { useError } from 'hooks/useError';
import { IArtistProfile } from 'hooks/swrRequests/useArtistProfiles';
// api
import api from 'utils/api';
// validations
import { getServerFieldErrors, getStepSchema, parseDobInput } from '../validation';
import { ProfileScalarKey, ProfileStep, getEditableFields } from '../steps';
// styles
import { fontStyles } from 'styles/font-styles';
import { colors } from 'styles/colors';
import { useTranslation } from 'react-i18next';

interface OwnProps {
  step: ProfileStep;
  profile: IArtistProfile;
  accountEmail?: string;
  /** Unsaved values stashed on a previous visit to this step (page-lifetime only). */
  draft?: Record<string, string>;
  /** Stash of dirty values on unmount; `null` clears the stash after a save. */
  onDraft: (values: Record<string, string> | null) => void;
  onBack: (() => void) | null;
  /** Continue pressed — fields saved, `updated` is the server's profile. */
  onContinue: (updated: IArtistProfile) => void;
  /** Banking proof uploaded (no step advance). */
  onProfileChange: (updated: IArtistProfile) => void;
}

const ReadOnlyFieldRow: React.FC<{ label: React.ReactNode; value?: string | null }> = ({
  label,
  value,
}) => (
  <Box sx={{ py: 1.5, borderBottom: '1px solid #E9E9E9' }}>
    <Typography sx={{ fontSize: 16, color: colors.text.light.title, mb: 0.8 }}>{label}</Typography>
    <Typography sx={{ fontSize: 18, color: '#6B6B6B' }}>{value || '—'}</Typography>
  </Box>
);

const StepForm: React.FC<OwnProps> = ({
  step,
  profile,
  accountEmail,
  draft,
  onDraft,
  onBack,
  onContinue,
  onProfileChange,
}) => {
  const { t } = useTranslation();
  const { message, setError } = useError();

  const [isSubmitting, setSubmitting] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);

  const defaultValues = useMemo(
    () => ({
      ...getEditableFields(step).reduce<Record<string, string>>((values, field) => {
        const stored =
          profile[field.key as ProfileScalarKey] ||
          (field.prefillFrom ? profile[field.prefillFrom] : null);
        // native date inputs require exactly YYYY-MM-DD
        values[field.key] =
          field.date && stored ? dayjs(stored).format('YYYY-MM-DD') : stored ?? '';
        return values;
      }, {}),
      // dirty values from a previous visit win over the saved profile
      ...draft,
    }),
    [step, profile, draft]
  );

  const form = useForm({ defaultValues, resolver: yupResolver(getStepSchema(step, t)) });

  // formState is a Proxy — isDirty must be READ DURING RENDER to be tracked at all;
  // mirror it into a ref for the unmount stash below.
  const { isDirty } = form.formState;
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  // stash dirty values so they survive this form's unmount (step/artist switch)
  const onDraftRef = useRef(onDraft);
  onDraftRef.current = onDraft;
  useEffect(
    () => () => {
      if (isDirtyRef.current) onDraftRef.current(form.getValues());
    },
    // the useForm instance is stable for the component's lifetime
    [form]
  );

  const onSubmit = async (formValues: any) => {
    try {
      setSubmitting(true);
      const data: Record<string, string | null> = {};
      getEditableFields(step).forEach((field) => {
        const raw = formValues[field.key];
        const trimmed = typeof raw === 'string' ? raw.trim() : raw;
        if (field.date) {
          data[field.key] = trimmed ? parseDobInput(String(trimmed)).format('YYYY-MM-DD') : null;
        } else {
          data[field.key] = trimmed ? String(trimmed) : null;
        }
      });
      const updated = await api.artistProfile.update<IArtistProfile>({ id: profile.id, data });
      // saved — drop the draft and mark the form pristine so unmount doesn't re-stash
      onDraft(null);
      form.reset(formValues);
      onContinue(updated);
    } catch (err) {
      const stepKeys = new Set<string>(getEditableFields(step).map((field) => field.key));
      const fieldErrors = (getServerFieldErrors(err, t) || []).filter(({ field }) =>
        stepKeys.has(field)
      );
      if (fieldErrors.length) {
        fieldErrors.forEach(({ field, message: text }) =>
          form.setError(field, { type: 'server', message: text })
        );
      } else {
        setError(err);
        setOpenErrorModal(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{}}>
      <FormProvider {...form}>
        <form noValidate onSubmit={form.handleSubmit(onSubmit)}>
          <SectionCard title={t(step.section ?? step.label)}>
            {step.intro && (
              <Typography sx={{ fontSize: 16, color: '#8C8C8C', mb: 2.5 }}>
                {t(step.intro)}
              </Typography>
            )}
            <Stack spacing={2}>
              {(step.fields || []).map((field) =>
                field.readOnly ? (
                  <ReadOnlyFieldRow
                    key={field.key}
                    label={
                      <>
                        {t(field.label)}{' '}
                        {field.required && (
                          <Box component="span" sx={{ color: colors.system.error }}>
                            *
                          </Box>
                        )}
                      </>
                    }
                    value={
                      field.accountScope
                        ? accountEmail
                        : profile[field.key as keyof IArtistProfile]?.toString()
                    }
                  />
                ) : (
                  <StepField
                    key={field.key}
                    field={field}
                    defaultValue={defaultValues[field.key]}
                    storedValue={profile[field.key as ProfileScalarKey]}
                  />
                )
              )}
              {step.withWelcomeGift && (
                <WelcomeGiftBlock profile={profile} defaultValues={defaultValues} />
              )}
              {step.withUpload && (
                <BankingProofUpload profile={profile} onUploaded={onProfileChange} />
              )}
            </Stack>
          </SectionCard>
          <Stack direction="row" alignItems="center" sx={{ mt: 3.5 }}>
            {onBack && (
              <Button
                variant="outlined"
                color="inherit"
                onClick={onBack}
                sx={{
                  ...fontStyles.body,
                  textTransform: 'none',
                  height: '48px',
                  borderColor: '#E9E9E9',
                  px: 2.7,
                }}
              >
                ← {t('Back')}
              </Button>
            )}
            <PurpleButton
              type="submit"
              loading={isSubmitting}
              sx={{ ml: 'auto', width: 'auto', px: 3.2, height: '48px' }}
            >
              {t('Continue')} →
            </PurpleButton>
          </Stack>
        </form>
      </FormProvider>
      <ErrorModal
        open={openErrorModal}
        handleClose={() => setOpenErrorModal(false)}
        message={message}
      />
    </Box>
  );
};

export default StepForm;
